import React, { useEffect, useState } from "react";
import axios from "axios";
import { Users, UserPlus, Mail, X, Crown, Send, Clock, ArrowRight, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PageHeader, Spinner } from "../components/Brand";
import { useApp, API } from "../AppContext";

export default function Team() {
  const { lang } = useApp();
  const nav = useNavigate();
  const isFr = lang === "fr";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: "", name: "" });
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/team/members`);
      setData(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Erreur");
    }
  };
  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    setErr(""); setOk(""); setLoading(true);
    try {
      await axios.post(`${API}/team/invite`, form);
      setOk(isFr ? `Invitation envoyée à ${form.email}` : `Invitation sent to ${form.email}`);
      setForm({ email: "", name: "" });
      await load();
      setTimeout(() => setOk(""), 3500);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  const cancelInvite = async (id) => {
    try { await axios.post(`${API}/team/invites/${id}/cancel`); await load(); } catch (e) { setErr(e?.response?.data?.detail || "Erreur"); }
  };

  const removeMember = async (id) => {
    if (!window.confirm(isFr ? "Retirer ce membre de l'équipe ?" : "Remove this member?")) return;
    try { await axios.delete(`${API}/team/members/${id}`); await load(); } catch (e) { setErr(e?.response?.data?.detail || "Erreur"); }
  };

  if (!data) return <Spinner />;
  const { members, pending_invites, count, limit, is_owner } = data;
  const canInvite = is_owner && (count + (pending_invites?.length || 0)) < limit;
  const pctUsed = Math.min(100, (count / limit) * 100);

  return (
    <div data-testid="team-page">
      <PageHeader
        title={isFr ? "Équipe" : "Team"}
        subtitle={isFr ? "Invitez vos employés par courriel. Chaque membre a son propre accès personnel." : "Invite your employees by email. Each member has their own personal access."}
      />

      {/* Usage card */}
      <div className="glass-card p-5 mb-6 max-w-3xl" data-testid="team-usage">
        <div className="flex items-baseline justify-between mb-3">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color: "#D4AF37" }}>
            {isFr ? "Utilisateurs" : "Users"}
          </div>
          <div className="text-lg font-bold" style={{ color: "#F5F5F5" }}>
            {count}<span style={{ color: "rgba(245,245,245,0.5)" }}> / {limit}</span>
          </div>
        </div>
        <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: "rgba(245,245,245,0.08)" }}>
          <div className="h-full rounded-full transition-all"
            style={{ width: `${pctUsed}%`, background: pctUsed >= 100 ? "#C00000" : pctUsed >= 80 ? "#F5D062" : "#D4AF37", boxShadow: pctUsed >= 100 ? "0 0 8px rgba(192,0,0,0.5)" : "0 0 8px rgba(212,175,55,0.4)" }} />
        </div>
        {!canInvite && is_owner && (
          <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs" style={{ color: "rgba(245,245,245,0.65)" }}>
              {isFr ? "Limite de votre forfait atteinte." : "Plan limit reached."}
            </div>
            <button onClick={() => nav("/app/billing")} className="btn-gold text-xs" data-testid="team-upgrade">
              <Sparkles size={12} /> {isFr ? "Passer au forfait supérieur" : "Upgrade plan"} <ArrowRight size={12} />
            </button>
          </div>
        )}
      </div>

      {/* Invite form (owner only) */}
      {is_owner && (
        <div className="glass-card p-6 mb-6 max-w-3xl" data-testid="team-invite-form">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "rgba(212,175,55,0.15)", border: "1px solid rgba(212,175,55,0.35)" }}>
              <UserPlus size={16} color="#D4AF37" />
            </div>
            <h3 className="font-heading text-lg font-bold" style={{ color: "#F5F5F5" }}>
              {isFr ? "Inviter un membre" : "Invite a member"}
            </h3>
          </div>
          <form onSubmit={invite} className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input type="email" required className="input-dark sm:col-span-1"
              placeholder={isFr ? "courriel@entreprise.ca" : "email@company.ca"}
              value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              disabled={!canInvite} data-testid="team-invite-email" />
            <input className="input-dark sm:col-span-1"
              placeholder={isFr ? "Nom (facultatif)" : "Name (optional)"}
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              disabled={!canInvite} data-testid="team-invite-name" />
            <button type="submit" disabled={loading || !canInvite}
              className="btn-gold justify-center disabled:opacity-50" data-testid="team-invite-submit">
              {loading ? <Spinner /> : <><Send size={13} /> {isFr ? "Envoyer l'invitation" : "Send invite"}</>}
            </button>
          </form>
          {err && (
            <div className="mt-3 text-sm px-3 py-2 rounded-xl" style={{ background: "rgba(192,0,0,0.15)", color: "#FF6B6B", border: "1px solid rgba(192,0,0,0.35)" }} data-testid="team-error">{err}</div>
          )}
          {ok && (
            <div className="mt-3 text-sm px-3 py-2 rounded-xl" style={{ background: "rgba(74,222,128,0.12)", color: "#4ADE80", border: "1px solid rgba(74,222,128,0.3)" }} data-testid="team-ok">{ok}</div>
          )}
        </div>
      )}

      {/* Members list */}
      <div className="glass-card p-6 mb-6 max-w-3xl" data-testid="team-members-list">
        <div className="flex items-center gap-2 mb-4">
          <Users size={16} color="#D4AF37" />
          <h3 className="font-heading text-lg font-bold" style={{ color: "#F5F5F5" }}>
            {isFr ? "Membres" : "Members"}
          </h3>
          <span className="text-xs ml-1 px-2 py-0.5 rounded-full" style={{ background: "rgba(212,175,55,0.15)", color: "#D4AF37" }}>{count}</span>
        </div>
        <div className="space-y-2">
          {members.map((m) => {
            const isOwner = !m.owner_id;
            return (
              <div key={m.id} className="flex items-center justify-between gap-3 px-4 py-3 rounded-2xl"
                style={{ background: "rgba(11,11,11,0.5)", border: "1px solid rgba(245,245,245,0.06)" }}
                data-testid={`team-member-${m.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0"
                    style={{ background: isOwner ? "linear-gradient(180deg, #F5D062, #D4AF37)" : "rgba(212,175,55,0.15)", color: isOwner ? "#0B0B0B" : "#D4AF37", border: isOwner ? "none" : "1px solid rgba(212,175,55,0.3)" }}>
                    {(m.name || m.email).slice(0, 1).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold flex items-center gap-2 flex-wrap" style={{ color: "#F5F5F5" }}>
                      {m.name || m.email.split("@")[0]}
                      {isOwner && <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-full inline-flex items-center gap-1" style={{ background: "rgba(212,175,55,0.15)", color: "#D4AF37" }}><Crown size={9} /> {isFr ? "Propriétaire" : "Owner"}</span>}
                    </div>
                    <div className="text-xs truncate" style={{ color: "rgba(245,245,245,0.55)" }}>{m.email}</div>
                  </div>
                </div>
                {is_owner && !isOwner && (
                  <button onClick={() => removeMember(m.id)} className="p-2 rounded-full hover:bg-white/5" title={isFr ? "Retirer" : "Remove"} data-testid={`team-remove-${m.id}`}>
                    <X size={14} color="#FF6B6B" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Pending invites */}
      {pending_invites && pending_invites.length > 0 && (
        <div className="glass-card p-6 max-w-3xl" data-testid="team-pending-invites">
          <div className="flex items-center gap-2 mb-4">
            <Clock size={16} color="#D4AF37" />
            <h3 className="font-heading text-lg font-bold" style={{ color: "#F5F5F5" }}>
              {isFr ? "Invitations en attente" : "Pending invites"}
            </h3>
            <span className="text-xs ml-1 px-2 py-0.5 rounded-full" style={{ background: "rgba(212,175,55,0.15)", color: "#D4AF37" }}>{pending_invites.length}</span>
          </div>
          <div className="space-y-2">
            {pending_invites.map((inv) => (
              <div key={inv.id} className="flex items-center justify-between gap-3 px-4 py-3 rounded-2xl"
                style={{ background: "rgba(11,11,11,0.5)", border: "1px solid rgba(245,245,245,0.06)" }}
                data-testid={`invite-${inv.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "rgba(212,175,55,0.08)", border: "1px dashed rgba(212,175,55,0.3)" }}>
                    <Mail size={14} color="#D4AF37" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate" style={{ color: "#F5F5F5" }}>{inv.name || inv.email}</div>
                    <div className="text-xs truncate" style={{ color: "rgba(245,245,245,0.55)" }}>{inv.email} · {isFr ? "expire dans" : "expires in"} {Math.max(0, Math.ceil((new Date(inv.expires_at) - new Date()) / 86400000))} {isFr ? "jours" : "days"}</div>
                  </div>
                </div>
                {is_owner && (
                  <button onClick={() => cancelInvite(inv.id)} className="text-xs px-3 py-1.5 rounded-full hover:brightness-110"
                    style={{ background: "rgba(192,0,0,0.15)", color: "#FF6B6B", border: "1px solid rgba(192,0,0,0.3)" }}
                    data-testid={`invite-cancel-${inv.id}`}>
                    {isFr ? "Annuler" : "Cancel"}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
